import React, { CSSProperties, useContext, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import { TimelineItem, TimelineItemType } from '../../../hooks/posts';
import { addNodeOperationKey, keepFormOpen, operationIsSubmitting, UIOperationState, updateNodeOperationKey, useOperation } from '../../../store/operation';
import { TicketContext } from './TicketContext';
import { ensureSignedIn } from '../../../utils/webPage';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { ticketNotificationKey } from '../../../store/actionsHandler';
import { generateTrackingId } from '../../../utils/tools';
import TimelineComponent from './TimelineComponent';
import OperationNotification from './OperationNotification';
import VisibilityTransition from '../../shared/VisibilityTransition';
import CommentEditor, { CommentEditorHandles, NodeEditorOperation } from './CommentEditor';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { LogoPointer } from './TimelineItemComponent';
import StateChange from './StateChange';
import RichComment, { commentTitleColors } from './RichComment';
import RichSolution, { solutionTitleColors } from './RichSolution';
import { useHistory } from 'react-router-dom';
import { UrlParam } from '../../../constants/urlParams';
import { NodeType } from './RichNodeEditor';
import { ResourceStrings } from '../../../ResourceStrings';
import { ChildPost, HistoryItem, PostType, PrincipalId } from '../../../RootPost';
import RouteInfo from './RouteInfo';

export const isChildTimeline = (timelineDepth?: number): boolean => timelineDepth && timelineDepth > 0;

const isEditableItem = (timelineItemType: TimelineItemType): boolean =>
    timelineItemType === TimelineItemType.comment || timelineItemType === TimelineItemType.solution;

const timelineItemTypeToFeedbackNodeType = (timelineItemType: TimelineItemType): NodeType => {
    switch (timelineItemType) {
        case TimelineItemType.comment: return NodeType.comment;
        case TimelineItemType.solution: return NodeType.solution;
        default:
            throw Error("Given timeline item type can't be mapped to a node type");
    }
}

export interface ElementColor {
    backgroundColor: string;
    textColor: string;
}

export interface FeedbackTimelineComponentProps {
    idPrefix: string;
    timelineDepth?: number;
    timelineItems: TimelineItem[];
    parentNodeId: number;
    addNewCommentButtonLabel?: string;
    canContainSolutions?: boolean;
    canContainComments?: boolean;
    canContainReactions?: boolean;
    forbidAddNodeOperations?: boolean;     // don't show comments or solutions are readonly messages
    className?: string;
    showNeedMoreInfoLink?: boolean;
    currentUserId: PrincipalId;
    fillMissingInfoClicked?: () => void;
}

export default function FeedbackTimelineComponent(props: FeedbackTimelineComponentProps): JSX.Element {
    const { timelineDepth, timelineItems, className, canContainSolutions, canContainComments, canContainReactions, forbidAddNodeOperations } = props;

    const refAddCommentEditor = useRef<CommentEditorHandles>();
    const refEditCommentEditor = useRef<CommentEditorHandles>();

    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const signInState = useSelector((state: RootState) => state.appState.signInState);
    const isUserBanned = useSelector((state: RootState) => state.appState.isUserBanned);
    const [newNodeTrackingId, setNewNodeTrackingId] = useState<string>(null);
    const [nodeIdEditing, setNodeIdEditing] = useState<number | null>(null);
    const [nodeIdEdited, setNodeIdEdited] = useState<number | null>(null);
    const history = useHistory();

    const ticketDetailsCtx = useContext(TicketContext);
    const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;

    const addNodeOpKey = addNodeOperationKey(ticket?.id, props.parentNodeId);
    const addNodeOperation = useOperation(addNodeOpKey);

    const updateNodeOpKey = updateNodeOperationKey(ticket.id, props.parentNodeId);
    const updateNodeOperation = useOperation(updateNodeOpKey);

    const canAddCommentForTicketState = ticketDetailsCtx?.ticketDetails?.canAddComment;
    const canAddComment = canContainComments && canAddCommentForTicketState;
    const canAddReactionForTicketState = ticketDetailsCtx?.ticketDetails?.canAddReaction;
    const canAddReaction = canContainReactions && canAddReactionForTicketState;
    const canAddSolutionForTicketState = ticketDetailsCtx?.ticketDetails?.canAddSolution;    // Whether user is allowed to add solutions given the state of ticket
    const canAddSolution = canContainSolutions && canAddSolutionForTicketState;
    const canAddCommentOrReaction = canAddComment || canAddReaction;
    const canAddNode = !forbidAddNodeOperations && (canAddCommentOrReaction || canAddSolution);

    const showCommentReadonlyMessage = (canContainComments && !canAddCommentForTicketState) || (canContainReactions && !canAddReactionForTicketState);
    const showSolutionReadonlyMessage = canContainSolutions && !canAddSolutionForTicketState;

    const showAddNewNodeDialogButtonClicked = (): void => {
        if (ensureSignedIn(signInState, ticketNotificationKey(ticket?.id), "Please sign in to post comments and solutions.")) {
            setNewNodeTrackingId(generateTrackingId());
            updateNodeOperation.resetOperation();
            addNodeOperation.resetOperation();
            addNodeOperation.setState(UIOperationState.InProgress);

            setTimeout(() => {
                refAddCommentEditor.current?.focus();
            }, 200);
        }
    }

    const cancelAddNodeClicked = (): void => {
        addNodeOperation.resetOperation();
    }

    const showEditNodeClicked = (timelineItem: TimelineItem): void => {
        if (timelineItem.type !== TimelineItemType.comment && timelineItem.type !== TimelineItemType.solution) {
            console.error("Invalid node type for editing: " + timelineItem.type);
            return;
        }

        const node = timelineItem.data as ChildPost;
        setNodeIdEditing(node.id);
        setNodeIdEdited(node.id);
        addNodeOperation.resetOperation();
        updateNodeOperation.resetOperation();
        updateNodeOperation.setState(UIOperationState.InProgress);

        setTimeout(() => {
            refEditCommentEditor.current?.focus();
        }, 500);
    }

    const cancelEditNodeClicked = (): void => {
        updateNodeOperation.resetOperation();
        setNodeIdEditing(null);
        setNodeIdEdited(null);
    }

    const originalNmiCommentId = ticketDetailsCtx?.ticketDetails?.originalNmiComment?.id;
    const showAddNMIEditor = useMemo(() => {
        const params = new URLSearchParams(history.location.search);
        return params.get(UrlParam.ProvideMoreInfo) === "true";
    }, [history.location.pathname, history.location.search]);

    return <Wrapper className={`timeline-item ${className ?? ''}`}>
        <TimelineComponent
            timelineItems={timelineItems}
            renderItem={(timelineItem => {
                const nodeId = timelineItem.type === TimelineItemType.comment || timelineItem.type === TimelineItemType.solution ? (timelineItem.data as ChildPost).id : null;
                const showNodeEditor = nodeIdEditing && keepFormOpen(updateNodeOperation.operation) && nodeId === nodeIdEditing;
                const showNMIEditorFinal = originalNmiCommentId && originalNmiCommentId === nodeId && showAddNMIEditor;
                let titleColor: ElementColor;

                switch (timelineItem.type) {
                    case TimelineItemType.solution:
                        titleColor = solutionTitleColors(timelineItem.data as ChildPost, ticket);
                        break;
                    case TimelineItemType.comment:
                        titleColor = commentTitleColors(timelineItem.data as ChildPost, ticket, props.showNeedMoreInfoLink && originalNmiCommentId && originalNmiCommentId === nodeId);
                        break;
                    default:
                        titleColor = { backgroundColor: "var(--color-background)", textColor: "auto" };
                        break;
                }

                if (!titleColor) {
                    titleColor = { backgroundColor: "var(--color-background)", textColor: "auto" };
                }

                if (nodeIdEditing && keepFormOpen(updateNodeOperation.operation) && nodeId === nodeIdEditing) {
                    titleColor = { backgroundColor: "var(--color-background)", textColor: "var(--color-background)" };
                }

                return <div
                    style={
                        titleColor && {
                            "--titlebar-background-color": titleColor.backgroundColor,
                            "--titlebar-text-color": titleColor.textColor,
                            "--timeline-header-min-height": timelineItem.type === TimelineItemType.comment ? "2.9rem" : "3.8rem",
                            "--timeline-logo-pointer-size": timelineItem.type === TimelineItemType.comment ? "1.4rem" : "1.6rem",
                        } as CSSProperties
                    }
                >
                    <LogoPointer timelineDepth={timelineDepth} />

                    {timelineItem.type === TimelineItemType.routeInfo && (
                        <RouteInfo
                            actions={timelineItem.data as string[]}
                            ticket={ticketDetailsCtx.ticketDetails?.ticket?.data} />
                    )}

                    {timelineItem.type === TimelineItemType.stateChange && (
                        <StateChange
                            stateChangeEntry={timelineItem.data as HistoryItem}
                            ticket={ticketDetailsCtx.ticketDetails?.ticket?.data} />
                    )}

                    {!showNodeEditor && timelineItem.type === TimelineItemType.comment && (
                        <div>
                            <RichComment
                                idPrefix={props.idPrefix}
                                comment={(timelineItem.data as ChildPost)}
                                timelineItem={timelineItem}
                                showNMIEditor={showNMIEditorFinal}
                                currentUserId={props.currentUserId}
                                showNeedMoreInfoLink={props.showNeedMoreInfoLink && originalNmiCommentId && originalNmiCommentId === nodeId}
                                fillMissingInfoClicked={props.fillMissingInfoClicked}
                                onEdit={(comment: ChildPost) => showEditNodeClicked(timelineItem)}
                            />
                        </div>
                    )}

                    {!showNodeEditor && timelineItem.type === TimelineItemType.solution && (
                        <div>
                            <RichSolution
                                idPrefix={props.idPrefix}
                                item={(timelineItem.data as ChildPost)}
                                timelineItem={timelineItem}
                                onEdit={(solution: ChildPost) => showEditNodeClicked(timelineItem)}
                            />
                        </div>
                    )}

                    {nodeId === nodeIdEdited && (
                        <OperationNotificationWrapper
                            operation={updateNodeOperation.operation}
                            resetOperation={updateNodeOperation.resetOperation}
                            className="node-edited-message" />
                    )}

                    <VisibilityTransition showIf={nodeIdEditing && keepFormOpen(updateNodeOperation.operation) && nodeId === nodeIdEditing}>
                        <CommentEditorWrapper insideTimeline>
                            <CommentEditor
                                ref={refEditCommentEditor}
                                operation={NodeEditorOperation.Modify}
                                uiOperationKey={updateNodeOpKey}
                                nodeType={isEditableItem(timelineItem.type) ? timelineItemTypeToFeedbackNodeType(timelineItem.type) : null}
                                nodeToModify={timelineItem.data as ChildPost}
                                parentNodeId={props.parentNodeId}
                                currentUserId={props.currentUserId}
                                isSubmitting={operationIsSubmitting(updateNodeOperation.operation)}
                                onCancel={cancelEditNodeClicked}
                                setNewNodeTrackingId={setNewNodeTrackingId}
                            />
                        </CommentEditorWrapper>
                    </VisibilityTransition>
                </div>
            })}
        >
            <>
                {/* Add node */}
                <OperationNotificationWrapper
                    operation={addNodeOperation.operation}
                    resetOperation={addNodeOperation.resetOperation}
                    className="node-added-message"
                />

                <VisibilityTransition showIf={canAddNode && keepFormOpen(addNodeOperation.operation)}>
                    <CommentEditorWrapper>
                        <CommentEditor
                            ref={refAddCommentEditor}
                            operation={NodeEditorOperation.Add}
                            uiOperationKey={addNodeOpKey}
                            parentNodeId={props.parentNodeId /* @TODO:  | ticket.id*/}
                            newNodeTrackingId={newNodeTrackingId}
                            disableAddComment={!canAddCommentOrReaction}
                            disableAddSolution={!canAddSolution}
                            currentUserId={props.currentUserId}
                            isSubmitting={operationIsSubmitting(addNodeOperation.operation)}
                            onCancel={cancelAddNodeClicked}
                            setNewNodeTrackingId={setNewNodeTrackingId}
                        />
                    </CommentEditorWrapper>
                </VisibilityTransition>

                {!forbidAddNodeOperations && <>
                    {showCommentReadonlyMessage && !showSolutionReadonlyMessage && <CommentEditorMesageWrapper className="soln-readonly"><MessageBar messageBarType={MessageBarType.warning}>Comments are readonly</MessageBar></CommentEditorMesageWrapper>}
                    {!showCommentReadonlyMessage && showSolutionReadonlyMessage && <CommentEditorMesageWrapper className="soln-readonly"><MessageBar messageBarType={MessageBarType.warning}>Solutions are readonly</MessageBar></CommentEditorMesageWrapper>}
                    {showCommentReadonlyMessage && showSolutionReadonlyMessage && <CommentEditorMesageWrapper className="soln-readonly"><MessageBar messageBarType={MessageBarType.warning}>Comments and solutions are readonly</MessageBar></CommentEditorMesageWrapper>}
                </>}

                {canAddNode && !keepFormOpen(addNodeOperation.operation) && <CommentEditorMesageWrapper>
                    {props.showNeedMoreInfoLink && !isUserBanned && !isInReadOnlyMode && (
                        <Button
                            buttonType="primary"
                            onClick={props.fillMissingInfoClicked}>
                            {ResourceStrings.provideMoreInfo()}
                        </Button>
                    )}

                    {isUserBanned && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            You are banned from adding comments and solutions to Visual Studio Developer Community due to violations of our <a href="https://answers.microsoft.com/en-us/page/codeofconduct" target="_blank" rel="noopener noreferrer">Community Code of Conduct</a>.
                        </MessageBar>
                    )}

                    {!isUserBanned && (
                        <Button
                            disabled={isInReadOnlyMode}
                            buttonType={timelineDepth === 0 && !props.showNeedMoreInfoLink ? "primary" : "normal"}
                            className={timelineDepth === 0 ? "top-level-button" : "nested-button"}
                            onClick={() => showAddNewNodeDialogButtonClicked()}
                        >
                            {canAddCommentOrReaction && canAddSolution && <span>Add comment or solution</span>}
                            {canAddCommentOrReaction && !canAddSolution && <span>{props.addNewCommentButtonLabel ?? "Add comment"}</span>}
                            {!canAddCommentOrReaction && canAddSolution && <span>Add solution</span>}
                        </Button>
                    )}
                </CommentEditorMesageWrapper>}
            </>
        </TimelineComponent>
    </Wrapper >
}

const Wrapper = styled.div`
    pre {
        white-space: pre-wrap;
        word-break: break-all;}
`;

const CommentEditorWrapper = styled.div<{ insideTimeline?: boolean }>`
    padding-left: ${props => props.insideTimeline ? '0rem' : 'var(--timeline-logo-panel-width)'};
`;

const CommentEditorMesageWrapper = styled.div`
    padding-top: 1rem;
    padding-left: var(--timeline-logo-panel-width);

    .nested-button {
        font-size: 1.4rem;
        padding: .4rem 1.4rem;
        min-height: auto;
    }

    &.soln-readonly {
        font-weight: 600;
    }

    button + button {
        margin-left: 2rem;
    }
`;

const OperationNotificationWrapper = styled(OperationNotification)`
    && {
        margin-top: 2rem;
        padding-bottom: 0;
        margin-left: var(--timeline-logo-panel-width);
        margin-right: 0.2rem;
    }

    &.node-edited-message {
        margin-left: calc(var(--timeline-logo-to-body-gap) - .5rem);
    }
`;
